import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import chairmanReportsService from '../services/chairmanReportsService';
import {
  MdGroups,
  MdPerson,
  MdSettings,
  MdAssignment,
  MdArrowForward
} from 'react-icons/md';

const MODULE_ICONS = {
  hr: MdPerson,
  hro: MdGroups,
  admin: MdSettings
};

const assessmentModules = [
  {
    id: 'hr',
    title: 'HR Module Assessment',
    description: 'Review HR applications, interviews, attendance, and leave workflows.',
    icon: MdPerson,
    links: [
      { label: 'Applications', path: '/hr/applications' },
      { label: 'Interviews', path: '/hr/interviews' },
      { label: 'Attendance', path: '/hr/attendance' },
      { label: 'HR Leave', path: '/hr/leave' }
    ]
  },
  {
    id: 'hro',
    title: 'HRO Module Assessment',
    description: 'Assess HRO operations for leave approvals, shift assignment, and duty management.',
    icon: MdGroups,
    links: [
      { label: 'HRO Attendance', path: '/hro/attendance' },
      { label: 'Leave Requests', path: '/hro/leave-requests' },
      { label: 'Shift Assignment', path: '/hro/shift-assignment' },
      { label: 'Duty Management', path: '/hro/duty-management' }
    ]
  },
  {
    id: 'admin',
    title: 'Admin Module Assessment',
    description: 'Assess user management, job setup, resignations, and admin data operations.',
    icon: MdSettings,
    links: [
      { label: 'Candidates', path: '/admin/candidates' },
      { label: 'Create User', path: '/admin/create-user' },
      { label: 'Job Management', path: '/admin/job-management' },
      { label: 'Resignations', path: '/admin/resignations' }
    ]
  }
];

const ChairmanAssessmentPage = () => {
  const navigate = useNavigate();
  const [modules, setModules] = React.useState(assessmentModules);

  React.useEffect(() => {
    const loadModules = async () => {
      try {
        const response = await chairmanReportsService.getAssessmentModules();
        if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
          const normalizedModules = response.data.map((module) => ({
            ...module,
            icon: MODULE_ICONS[module.id] || MdAssignment
          }));
          setModules(normalizedModules);
        }
      } catch (error) {
        // Fallback to local defaults if endpoint is unavailable.
        setModules(assessmentModules);
      }
    };

    loadModules();
  }, []);

  return (
    <MainLayout>
      <div className="min-h-screen bg-background p-lg">
        <div className="mb-xl rounded-lg border border-border-light bg-surface p-lg shadow-sm">
          <div className="mb-2 flex items-center gap-2 text-accent-green">
            <MdAssignment size={22} />
            <span className="text-sm font-semibold uppercase tracking-wide">Chairman Assessment</span>
          </div>
          <h1 className="text-2xl font-semibold text-primary">Cross-Module Assessment Center</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Assess HR, HRO, and Admin modules from one place. This page only routes to existing module screens.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-lg lg:grid-cols-3">
          {modules.map((module) => (
            <div key={module.id} className="rounded-lg border border-border-light bg-surface p-lg shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <module.icon className="text-accent-green" size={24} />
                <h2 className="text-lg font-semibold text-primary">{module.title}</h2>
              </div>

              <p className="mb-4 text-sm text-text-secondary">{module.description}</p>

              <div className="space-y-2">
                {module.links.map((link) => (
                  <button
                    key={link.path}
                    type="button"
                    onClick={() => navigate(link.path)}
                    className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm font-medium text-primary transition hover:border-accent-green hover:bg-background-light"
                  >
                    <span>{link.label}</span>
                    <MdArrowForward size={16} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default ChairmanAssessmentPage;
